"use client";

import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type PointerEvent as ReactPointerEvent
} from "react";
import { Icon, Image, Modal, Text } from "@pamperedchef/design-system";
import * as styles from "./ImageGalleryModal.module.scss";

export interface ImageGalleryModalSlide {
	/** Resolved source URL — image or video. Plugin-agnostic. */
	src: string;
	alt: string;
	caption?: string;
	/** Poster URL used when `kind === "video"`. */
	poster?: string;
	/** Defaults to "image". */
	kind?: "image" | "video";
}

export interface ImageGalleryModalProps {
	open: boolean;
	slides: ImageGalleryModalSlide[];
	/** Index of the slide to show first when the modal opens. */
	initialIndex?: number;
	/** Title shown in the modal header. */
	title?: string;
	/** Called when the user requests close (escape, backdrop, ✕). */
	onClose: () => void;
	/** Maximum zoom multiplier for desktop hover-zoom. Default 1.5. */
	hoverZoom?: number;
	/**
	 * Fired the first time the selected video slide starts playing —
	 * once per selection, so pause/resume doesn't re-fire. Receives the
	 * slide's index within `slides`.
	 */
	onVideoPlay?: (index: number) => void;
}

function touchDistance(a: Touch, b: Touch) {
	return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function hintText() {
	return <>Pinch or tap to zoom</>;
}

// Native video player matching the See in Action block — a plain
// `<video controls>` with poster, no embedded third-party player chrome
// (which would render its own duplicate play affordance over the video).
function ModalVideoPlayer({
	src,
	poster,
	ariaLabel,
	onPlay
}: {
	src: string;
	poster?: string;
	ariaLabel?: string;
	onPlay?: () => void;
}) {
	return (
		<video
			key={src}
			className={styles["video-player"]}
			controls
			playsInline
			preload="metadata"
			src={src}
			poster={poster}
			aria-label={ariaLabel}
			onPlay={onPlay}
		/>
	);
}

interface ThumbnailButtonProps {
	slide: ImageGalleryModalSlide;
	index: number;
	isSelected: boolean;
	onClick: () => void;
	buttonClass: string;
	activeClass: string;
}

function ThumbnailButton({
	slide,
	index,
	isSelected,
	onClick,
	buttonClass,
	activeClass
}: ThumbnailButtonProps) {
	return (
		<button
			type="button"
			className={[buttonClass, isSelected ? activeClass : ""]
				.filter(Boolean)
				.join(" ")}
			aria-label={`View ${slide.kind === "video" ? "video" : "image"} ${index + 1}`}
			onClick={onClick}
		>
			<Image src={slide.poster ?? slide.src} alt="" aspectRatio="1:1" />
		</button>
	);
}

export function ImageGalleryModal({
	open,
	slides,
	initialIndex = 0,
	title,
	onClose,
	hoverZoom = 1.5,
	onVideoPlay
}: ImageGalleryModalProps) {
	const [selectedIndex, setSelectedIndex] = useState(
		Math.max(0, Math.min(initialIndex, slides.length - 1))
	);

	// Once-per-selection guard so pause/resume doesn't re-fire onVideoPlay.
	const videoPlayFiredRef = useRef(false);
	useEffect(() => {
		videoPlayFiredRef.current = false;
	}, [selectedIndex, open]);

	const handleVideoPlay = useCallback(() => {
		if (videoPlayFiredRef.current) return;
		videoPlayFiredRef.current = true;
		onVideoPlay?.(selectedIndex);
	}, [onVideoPlay, selectedIndex]);

	const [hovering, setHovering] = useState(false);
	const [hoverPos, setHoverPos] = useState<{ x: number; y: number }>({
		x: 50,
		y: 50
	});

	// Touch / pinch state
	const [touchScale, setTouchScale] = useState(1);
	const [touchOffset, setTouchOffset] = useState<{ x: number; y: number }>({
		x: 0,
		y: 0
	});
	// Mirror touch state in refs so native (non-passive) listeners always read
	// the latest values without re-binding on every gesture frame.
	const touchScaleRef = useRef(1);
	const touchOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const mobileMainRef = useRef<HTMLDivElement>(null);
	const lastTapRef = useRef<number>(0);
	const pinchStartRef = useRef<{
		distance: number;
		scale: number;
		offset: { x: number; y: number };
	} | null>(null);
	const panStartRef = useRef<{
		x: number;
		y: number;
		offset: { x: number; y: number };
	} | null>(null);

	// Reset slide to initial when the modal (re)opens
	useEffect(() => {
		if (open) {
			setSelectedIndex(
				Math.max(0, Math.min(initialIndex, slides.length - 1))
			);
		}
	}, [open, initialIndex, slides.length]);

	useEffect(() => {
		setHovering(false);
		touchScaleRef.current = 1;
		touchOffsetRef.current = { x: 0, y: 0 };
		setTouchScale(1);
		setTouchOffset({ x: 0, y: 0 });
	}, [selectedIndex]);

	// Keyboard navigation
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			else if (e.key === "ArrowLeft")
				setSelectedIndex((i) => Math.max(0, i - 1));
			else if (e.key === "ArrowRight")
				setSelectedIndex((i) => Math.min(slides.length - 1, i + 1));
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose, slides.length]);

	const onPointerMove = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (e.pointerType !== "mouse") return;
			const r = e.currentTarget.getBoundingClientRect();
			setHoverPos({
				x: ((e.clientX - r.left) / r.width) * 100,
				y: ((e.clientY - r.top) / r.height) * 100
			});
		},
		[]
	);

	const onPointerEnter = useCallback(
		(e: ReactPointerEvent<HTMLDivElement>) => {
			if (e.pointerType === "mouse") setHovering(true);
		},
		[]
	);

	const onPointerLeave = useCallback(() => setHovering(false), []);

	const updateScale = useCallback((next: number) => {
		touchScaleRef.current = next;
		setTouchScale(next);
	}, []);

	const updateOffset = useCallback((next: { x: number; y: number }) => {
		touchOffsetRef.current = next;
		setTouchOffset(next);
	}, []);

	// React synthetic touch listeners are passive, so e.preventDefault() is
	// silently ignored — that lets the browser handle the pinch as a native
	// page-zoom gesture and our handler never fires. Attach native listeners
	// with { passive: false } so the gesture is captured here instead.
	useEffect(() => {
		const el = mobileMainRef.current;
		if (!el || !open) return;

		const handleTouchStart = (e: TouchEvent) => {
			if (e.touches.length === 2) {
				e.preventDefault();
				pinchStartRef.current = {
					distance: touchDistance(e.touches[0]!, e.touches[1]!),
					scale: touchScaleRef.current,
					offset: touchOffsetRef.current
				};
			} else if (e.touches.length === 1) {
				if (touchScaleRef.current > 1) {
					updateScale(1);
					updateOffset({ x: 0, y: 0 });
				} else {
					updateScale(2);
				}
				if (touchScaleRef.current > 1) {
					panStartRef.current = {
						x: e.touches[0]!.clientX,
						y: e.touches[0]!.clientY,
						offset: touchOffsetRef.current
					};
				}
			}
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (e.touches.length === 2 && pinchStartRef.current) {
				e.preventDefault();
				const d = touchDistance(e.touches[0]!, e.touches[1]!);
				const factor = d / pinchStartRef.current.distance;
				const next = Math.max(
					1,
					Math.min(4, pinchStartRef.current.scale * factor)
				);
				updateScale(next);
				if (next === 1) updateOffset({ x: 0, y: 0 });
			} else if (
				e.touches.length === 1 &&
				panStartRef.current &&
				touchScaleRef.current > 1
			) {
				e.preventDefault();
				const dx = e.touches[0]!.clientX - panStartRef.current.x;
				const dy = e.touches[0]!.clientY - panStartRef.current.y;
				updateOffset({
					x: panStartRef.current.offset.x + dx,
					y: panStartRef.current.offset.y + dy
				});
			}
		};

		const handleTouchEnd = () => {
			pinchStartRef.current = null;
			panStartRef.current = null;
		};

		el.addEventListener("touchstart", handleTouchStart, { passive: false });
		el.addEventListener("touchmove", handleTouchMove, { passive: false });
		el.addEventListener("touchend", handleTouchEnd);
		el.addEventListener("touchcancel", handleTouchEnd);

		return () => {
			el.removeEventListener("touchstart", handleTouchStart);
			el.removeEventListener("touchmove", handleTouchMove);
			el.removeEventListener("touchend", handleTouchEnd);
			el.removeEventListener("touchcancel", handleTouchEnd);
		};
	}, [open, updateScale, updateOffset]);

	// ---- Derived styles --------------------------------------------------------
	const selectedSlide = slides[selectedIndex];

	const desktopImageStyle = useMemo<CSSProperties>(() => {
		if (!hovering || selectedSlide?.kind === "video") {
			return {
				transform: "none",
				transition: "transform 200ms ease-out"
			};
		}
		return {
			transform: `scale(${hoverZoom})`,
			transformOrigin: `${hoverPos.x}% ${hoverPos.y}%`,
			transition: "transform 120ms ease-out"
		};
	}, [hovering, hoverPos, hoverZoom, selectedSlide?.kind]);

	const mobileImageStyle = useMemo<CSSProperties>(() => {
		if (touchScale <= 1) {
			return {
				transform: "none",
				transition: "transform 200ms ease-out"
			};
		}
		return {
			transform: `translate(${touchOffset.x}px, ${touchOffset.y}px) scale(${touchScale})`,
			transition: "transform 80ms linear"
		};
	}, [touchScale, touchOffset]);

	if (!selectedSlide) return null;

	return (
		<Modal
			className={styles.modal}
			size="xl"
			title={title ?? ""}
			isOpen={open}
			setOpen={(v) => {
				const resolved = typeof v === "function" ? v(open) : v;
				if (!resolved) onClose();
			}}
			closeButtonClassName={styles["close-button"]}
		>
			<div className={styles["layouts-container"]}>
				<div className={styles.thumbnails}>
					{slides.map((slide, i) => (
						<ThumbnailButton
							key={`${slide.src}-${i}`}
							slide={slide}
							index={i}
							isSelected={i === selectedIndex}
							onClick={() => setSelectedIndex(i)}
							buttonClass={styles.thumbnail}
							activeClass={styles["thumbnail--active"]}
						/>
					))}
				</div>

				{selectedSlide.kind === "video" ? (
					<div className={styles["shared-video-player"]}>
						<ModalVideoPlayer
							src={selectedSlide.src}
							poster={selectedSlide.poster}
							ariaLabel={selectedSlide.alt}
							onPlay={handleVideoPlay}
						/>
					</div>
				) : (
					<div
						className={styles["main-image"]}
						onPointerMove={onPointerMove}
						onPointerEnter={onPointerEnter}
						onPointerLeave={onPointerLeave}
					>
						<div
							className={styles["image-zoom-wrapper"]}
							style={desktopImageStyle}
						>
							<Image
								src={selectedSlide.src}
								alt={selectedSlide.alt}
								className={styles["modal-image"]}
								objectFit="contain"
							/>
						</div>
					</div>
				)}

				<Text styledAs="xsr" className={styles["zoom-hint"]}>
					<Icon name="ZoomIn" size={12} />
					{selectedSlide.kind === "video"
						? "No zoom available for videos"
						: "Hover over image to zoom"}
				</Text>

				<div className={styles["mobile-layout"]}>
					<div
						ref={mobileMainRef}
						className={styles["mobile-main"]}
						style={{
							touchAction:
								selectedSlide.kind === "video" ? "auto" : "none"
						}}
					>
						{selectedSlide.kind !== "video" && (
							<div className={styles["mobile-image-wrap"]}>
								<Image
									key={selectedSlide.src}
									src={selectedSlide.src}
									alt={selectedSlide.alt}
									className={styles["mobile-image"]}
									objectFit="contain"
									style={{
										...mobileImageStyle,
										width: "auto",
										height: "auto"
									}}
									draggable={false}
								/>
							</div>
						)}

						<p className={styles["mobile-hint"]}>
							{selectedSlide.kind === "video"
								? selectedSlide.caption || ""
								: touchScale > 1
									? ""
									: hintText()}
						</p>
					</div>

					{slides.length > 1 && (
						<ul
							className={styles["mobile-thumbs"]}
							role="tablist"
							aria-label="Gallery thumbnails"
						>
							{slides.map((slide, i) => (
								<li
									key={`${slide.src}-${i}`}
									className={styles["mobile-thumb-item"]}
								>
									<button
										type="button"
										role="tab"
										aria-selected={i === selectedIndex}
										aria-label={`View slide ${i + 1}: ${slide.alt}`}
										onClick={() => setSelectedIndex(i)}
										className={[
											styles["mobile-thumb"],
											i === selectedIndex
												? styles["mobile-thumb--active"]
												: ""
										]
											.filter(Boolean)
											.join(" ")}
									>
										<Image
											src={slide.poster ?? slide.src}
											alt=""
											aspectRatio="1:1"
										/>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</Modal>
	);
}
